import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Drawer,
  Icon,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Slide,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
  alpha,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAppThemeContext, useAuthContext, useDrawerContext } from '../../contexts';
import { Environment } from '../../environment';
import { THEME_OPTIONS, MENU_ACCENT } from '../../themes';
import { DialogPefilFoto } from './DialogPefilFoto';
import { DialogPefil } from './DialogPerfil';
import { UsuarioService } from '../../services/api/cadastros/UsuariosService';

const RAIL_WIDTH = 100;
const FLYOUT_WIDTH = 248;

const getBaseRoute = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  return parts[0] || '';
};

const SYMBOLS_PREFIX = 'symbols:';

const ModuleIcon: React.FC<{ name: string }> = ({ name }) => {
  if (name.startsWith(SYMBOLS_PREFIX)) {
    return <Icon baseClassName="material-symbols-outlined">{name.slice(SYMBOLS_PREFIX.length)}</Icon>;
  }
  return <Icon>{name}</Icon>;
};

const ElegantDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'menucolor',
})<{ menucolor?: string | null }>(({ theme, menucolor }) => ({
  '& .MuiDrawer-paper': {
    width: RAIL_WIDTH,
    overflowX: 'hidden',
    backgroundColor: menucolor ?? alpha(theme.palette.background.paper, 0.95),
    borderRight: `1px solid ${alpha('#000000', 0.1)}`,
  },
}));

const RailButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'coloredbg',
})<{ coloredbg?: boolean }>(({ theme, coloredbg }) => ({
  width: 52,
  height: 52,
  borderRadius: theme.spacing(1.5),
  color: coloredbg ? alpha('#ffffff', 0.9) : alpha(theme.palette.text.primary, 0.7),
  transition: 'all 0.2s ease',
  '& .MuiIcon-root': {
    fontSize: '1.75rem',
  },
  '&:hover': {
    backgroundColor: coloredbg ? alpha('#ffffff', 0.14) : alpha(theme.palette.primary.main, 0.08),
    color: coloredbg ? '#ffffff' : theme.palette.text.primary,
  },
  '&.active': {
    backgroundColor: coloredbg ? alpha('#ffffff', 0.24) : alpha(theme.palette.primary.main, 0.16),
    color: coloredbg ? '#ffffff' : theme.palette.primary.main,
  },
}));

const FlyoutItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.25, 1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

interface IMenuLateralProps {
  children: React.ReactNode;
}

export const MenuLateral: React.FC<IMenuLateralProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [openModule, setOpenModule] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElTema, setAnchorElTema] = useState<null | HTMLElement>(null);
  const [openFoto, setOpenFoto] = useState(false);
  const [openPerfil, setOpenPerfil] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState('');
  const [idUser, setIdUser] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [loginUsuario, setLoginUsuario] = useState('');
  const openMenu = Boolean(anchorEl);
  const openMenuTema = Boolean(anchorElTema);

  const { isDrawerOpen, toggleDrawerOpen, drawerOptions, drawerItemOptions } = useDrawerContext();
  const { themeName, setThemeName } = useAppThemeContext();
  const { logout } = useAuthContext();
  const menuColor = MENU_ACCENT[themeName];
  const coloredMenu = menuColor !== null;

  interface JwtPayload {
    iduser: string;
    img: string;
  }

  useEffect(() => {
    const token = localStorage.getItem('APP_ACCESS_TOKEN');
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token);
      setFotoPerfil(Environment.URL_IMGUSUARIOS + decoded.img);
      setIdUser(decoded.iduser);
    }
  }, []);

  useEffect(() => {
    setOpenModule(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!idUser) return;
    UsuarioService.getByIdPerfil(idUser).then((result) => {
      if (!(result instanceof Error)) {
        setNomeUsuario(result.nome);
        setLoginUsuario(result.login);
      }
    });
  }, [idUser]);

  const currentBase = getBaseRoute(location.pathname);

  const isModuleActive = (modulePath: string) => {
    if (getBaseRoute(modulePath) === currentBase) return true;
    return drawerItemOptions.some(
      (item) => item.parent === modulePath && getBaseRoute(item.path) === currentBase
    );
  };

  const handleModuleClick = (modulePath: string, hasSubItems: boolean) => {
    if (hasSubItems) {
      setOpenModule((old) => (old === modulePath ? null : modulePath));
    } else {
      navigate(modulePath);
      setOpenModule(null);
      if (smDown) toggleDrawerOpen();
    }
  };

  const handleNavigateSubItem = (path: string) => {
    navigate(path);
    setOpenModule(null);
    if (smDown) toggleDrawerOpen();
  };

  const activeModuleOption = drawerOptions.find((d) => d.path === openModule);
  const activeSubItems = drawerItemOptions.filter((i) => i.parent === openModule);

  return (
    <>
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest?.('[data-rail-button]')) return;
          setOpenModule(null);
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <ElegantDrawer
            open={isDrawerOpen}
            variant={smDown ? 'temporary' : 'permanent'}
            onClose={toggleDrawerOpen}
            menucolor={menuColor}
          >
            <Box height="100%" display="flex" flexDirection="column" alignItems="center">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ pt: 2.5, pb: 1.5, px: 1, width: '100%' }}
              >
                <Avatar
                  src={fotoPerfil}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    width: 76,
                    height: 76,
                    cursor: 'pointer',
                    border: `2px solid ${coloredMenu ? alpha('#ffffff', 0.4) : alpha(theme.palette.primary.main, 0.25)}`,
                    '&:hover': {
                      borderColor: coloredMenu ? '#ffffff' : theme.palette.primary.main,
                    },
                  }}
                />
                <Tooltip title={loginUsuario}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    align="center"
                    noWrap
                    sx={{ mt: 1, maxWidth: '100%', lineHeight: 1.2, color: coloredMenu ? '#ffffff' : theme.palette.text.primary }}
                  >
                    {nomeUsuario ? nomeUsuario.split(' ')[0] : 'Usuário'}
                  </Typography>
                </Tooltip>
              </Box>

              <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
                <MenuItem
                  onClick={() => {
                    setOpenFoto(true);
                    setAnchorEl(null);
                  }}
                >
                  <Icon sx={{ mr: 1 }}>photo_camera</Icon>
                  Foto do Perfil
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOpenPerfil(true);
                    setAnchorEl(null);
                  }}
                >
                  <Icon sx={{ mr: 1 }}>person</Icon>
                  Perfil
                </MenuItem>
              </Menu>

              <Divider sx={{ width: '70%', mx: 'auto', mb: 1, borderColor: coloredMenu ? alpha('#ffffff', 0.2) : theme.palette.divider }} />

              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
                sx={{ pt: 1, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}
              >
                {drawerOptions.map((drawer) => {
                  const hasSubItems = drawerItemOptions.some((i) => i.parent === drawer.path);
                  const active = isModuleActive(drawer.path) || openModule === drawer.path;

                  return (
                    <Tooltip key={drawer.path} title={drawer.label} placement="right">
                      <RailButton
                        data-rail-button=""
                        coloredbg={coloredMenu}
                        className={active ? 'active' : ''}
                        onClick={() => handleModuleClick(drawer.path, hasSubItems)}
                      >
                        <ModuleIcon name={drawer.icon} />
                      </RailButton>
                    </Tooltip>
                  );
                })}
              </Box>

              <Divider sx={{ width: '70%', mx: 'auto', mb: 1, borderColor: coloredMenu ? alpha('#ffffff', 0.2) : theme.palette.divider }} />

              <Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{ pb: 2 }}>
                <Tooltip title="Empresa" placement="right">
                  <RailButton
                    coloredbg={coloredMenu}
                    className={getBaseRoute(location.pathname) === 'empresa' ? 'active' : ''}
                    onClick={() => {
                      navigate('/empresa');
                      setOpenModule(null);
                      if (smDown) toggleDrawerOpen();
                    }}
                  >
                    <Icon>settings</Icon>
                  </RailButton>
                </Tooltip>

                <Tooltip title="Tema" placement="right">
                  <RailButton coloredbg={coloredMenu} onClick={(e) => setAnchorElTema(e.currentTarget)}>
                    <Icon>palette</Icon>
                  </RailButton>
                </Tooltip>

                <Menu anchorEl={anchorElTema} open={openMenuTema} onClose={() => setAnchorElTema(null)}>
                  {THEME_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.name}
                      selected={themeName === opt.name}
                      onClick={() => {
                        setThemeName(opt.name);
                        setAnchorElTema(null);
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: opt.swatch,
                          mr: 1.5,
                          border: '1px solid rgba(0,0,0,0.15)',
                        }}
                      />
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>

                <Tooltip title="Sair" placement="right">
                  <RailButton coloredbg={coloredMenu} onClick={logout}>
                    <Icon>exit_to_app</Icon>
                  </RailButton>
                </Tooltip>
              </Box>
            </Box>
          </ElegantDrawer>

          <Slide direction="right" in={Boolean(openModule)} mountOnEnter unmountOnExit>
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: `${RAIL_WIDTH}px`,
                width: FLYOUT_WIDTH,
                zIndex: theme.zIndex.drawer + 2,
                borderRadius: 0,
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.82)
                    : alpha(theme.palette.background.paper, 0.85),
                backdropFilter: 'blur(12px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5 }}>
                <ListItemText
                  primary={activeModuleOption?.label}
                  primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                />
              </Box>
              <Divider />
              <List sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
                {activeSubItems.map((sub) => {
                  const subActive = getBaseRoute(location.pathname) === getBaseRoute(sub.path);
                  return (
                    <FlyoutItemButton
                      key={sub.path}
                      selected={subActive}
                      onClick={() => handleNavigateSubItem(sub.path)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon
                          sx={{
                            color: subActive
                              ? theme.palette.primary.main
                              : alpha(theme.palette.text.primary, 0.7),
                          }}
                        >
                          {sub.icon}
                        </Icon>
                      </ListItemIcon>
                      <ListItemText primary={sub.label} />
                    </FlyoutItemButton>
                  );
                })}
              </List>
            </Paper>
          </Slide>
        </Box>
      </ClickAwayListener>

      <DialogPefilFoto
        id={idUser}
        img={fotoPerfil}
        value=""
        open={openFoto}
        onClose={() => setOpenFoto(false)}
      />

      <DialogPefil
        id={idUser}
        value=""
        open={openPerfil}
        onClose={() => setOpenPerfil(false)}
      />

      <Box
        height="100vh"
        marginLeft={smDown ? 0 : `${RAIL_WIDTH}px`}
        sx={{ transition: 'margin 0.3s' }}
      >
        {children}
      </Box>
    </>
  );
};
